import { Button } from "@exopengithub/exokit/v2";
import { LoadingState } from "molecules/loadingState/LoadingState";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { useTranslation } from "react-i18next";

import { IconChevronRight, IconExternalLink } from "@tabler/icons-react";
import {
	useCreateReportDatabaseMutation,
	useCreateReportEwrMutation,
	useGetReportStatusQuery,
} from "../../common/redux/api/exopenApi";

import { Card } from "../planning/components/Card";
import { ButtonLink } from "../../common/components/atoms/button/ButtonLink.js";
import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.js";
import { useEwrLinkClick } from "src/common/service/useEwrLinkClick.js";
import type { ReportModule } from "src/common/service/ewr.js";

// Technical detail, superadmin-only page: which report-database schema modules
// are actually deployed. Silent when there's nothing to show (loading, error,
// or genuinely zero modules) rather than adding a spinner/error state.
const DeployedReportModules = ({
	modules,
}: {
	modules: ReportModule[] | undefined;
}) => {
	const { t } = useTranslation();
	if (!modules || modules.length === 0) {
		return null;
	}
	return (
		<div className="mb-4 text-sm text-gray-600">
			{t("Deployed report modules")}:{" "}
			{modules.map((module) => module.name).join(", ")}
		</div>
	);
};

export const Ewr = () => {
	const { t } = useTranslation();

	const companyDomain = useLoadedCompanyDomain();

	const { data: reportStatus, isLoading } = useGetReportStatusQuery({
		companyDomainId: `${companyDomain.id}`,
	});

	const { onClick: handleEwrClick, isChecking } = useEwrLinkClick(
		reportStatus?.ewrUrl ?? "",
	);

	const [createReportDatabase, { isLoading: isLoadingReportDb }] =
		useCreateReportDatabaseMutation();
	const [createEwr, { isLoading: isLoadingEwr }] = useCreateReportEwrMutation();

	const createNewReportDatabase = async () => {
		const result = await createReportDatabase({
			companyDomainId: `${companyDomain.id}`,
		});
		if ("error" in result) {
			showErrorNotification({
				message: t("Failed to create Report system database"),
			});
		} else {
			showSuccessNotification({
				message: t("Starting to create Report system database"),
			});
		}
	};

	const createNewEwr = async () => {
		const result = await createEwr({
			companyDomainId: `${companyDomain.id}`,
		});
		if ("error" in result) {
			showErrorNotification({
				message: t("Failed to start creation of the EWR"),
			});
		} else {
			showSuccessNotification({ message: t("Starting to create the EWR") });
		}
	};

	// WHY gate: the report status is its own request, so without this the page
	// flashes "create a Report database" at domains that already have one.
	if (isLoading) {
		return <LoadingState />;
	}

	if (!reportStatus?.hasReport) {
		return (
			<Card className="p-4">
				<div className="mb-4 text-base">
					{t("Start by creating a Report database")}
				</div>
				<Button
					isLoading={isLoadingReportDb}
					onClick={() => {
						createNewReportDatabase();
					}}
					icon={<IconChevronRight />}
					iconPosition="trailing"
				>
					{t("Create Report system database")}
				</Button>
			</Card>
		);
	}

	if (!reportStatus.ewrUrl) {
		return (
			<Card className="p-4">
				<DeployedReportModules modules={reportStatus.reportModules} />
				<div className="mb-4 text-base">{t("Report database created")}</div>
				<Button
					isLoading={isLoadingEwr}
					onClick={() => {
						createNewEwr();
					}}
					icon={<IconChevronRight />}
					iconPosition="trailing"
				>
					{t("Create EWR")}
				</Button>
			</Card>
		);
	}

	return (
		<Card className="w-full p-4">
			<DeployedReportModules modules={reportStatus.reportModules} />
			<div className="space-x-4">
				<ButtonLink
					to={reportStatus.ewrUrl}
					target="_blank"
					onClick={handleEwrClick}
					isLoading={isChecking}
					icon={<IconExternalLink />}
					iconPosition="right"
				>
					{t("Open EWR (in new tab)")}
				</ButtonLink>
			</div>
		</Card>
	);
};
